import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DELAY_CODE_OPTIONS } from '../constants/delayCodes';
import {
  createDelayAllocation,
  ensureDelayAllocationRows,
  finalizeDelayClock,
  formatDelayMinutes,
  getEnteredDelayMinutes,
  parseDelayClock
} from '../utils/flightDelays';
import './FlightDelayAllocations.css';

const FlightDelayAllocations = ({ calculatedMinutes, allocations, onChange, readOnly = false }) => {
  const [openRow, setOpenRow] = useState(null);
  const [search, setSearch] = useState('');
  const sectionRef = useRef(null);
  const calculated = Math.max(0, Number(calculatedMinutes) || 0);
  const entered = getEnteredDelayMinutes(allocations);

  useEffect(() => {
    if (readOnly || !onChange) return;
    onChange(current => ensureDelayAllocationRows(current, calculated));
  }, [calculated, onChange, readOnly]);

  useEffect(() => {
    const closeDropdown = event => {
      if (sectionRef.current && !sectionRef.current.contains(event.target)) {
        setOpenRow(null);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', closeDropdown);
    return () => document.removeEventListener('mousedown', closeDropdown);
  }, []);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return DELAY_CODE_OPTIONS;
    return DELAY_CODE_OPTIONS.filter(option => (
      `${option.code} ${option.reason} ${option.description}`.toLowerCase().includes(query)
    ));
  }, [search]);

  const commitRows = updater => {
    if (!onChange) return;
    onChange(current => ensureDelayAllocationRows(updater(current), calculated));
  };

  const updateRow = (index, patch) => {
    commitRows(current => current.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
  };

  const commitTime = (index, parsed) => {
    const otherMinutes = getEnteredDelayMinutes((allocations || []).filter((_, rowIndex) => rowIndex !== index));
    const available = Math.max(0, calculated - otherMinutes);
    const nextMinutes = parsed.minutes === '' ? '' : String(Math.min(Number(parsed.minutes), available));
    updateRow(index, {
      delay_minutes: nextMinutes,
      time_input: nextMinutes === '' ? '' : formatDelayMinutes(nextMinutes)
    });
  };

  const removeRow = index => {
    commitRows(current => {
      const next = current.filter((_, rowIndex) => rowIndex !== index);
      return next.length ? next : [createDelayAllocation()];
    });
  };

  const visibleRows = readOnly
    ? (allocations || [])
    : ensureDelayAllocationRows(allocations, calculated);

  return (
    <section className="flight-delay-list" ref={sectionRef}>
      {visibleRows.map((allocation, index) => {
        const selectedOption = DELAY_CODE_OPTIONS.find(option => option.code === allocation.delay_code);
        const isLastCard = index === visibleRows.length - 1;
        return (
          <div className="fd-delay-card flight-delay-allocation-card" key={allocation._key || allocation.id || index}>
            {visibleRows.length > 1 ? <div className="flight-delay-card-number">Delay {index + 1}</div> : null}
            {!readOnly && visibleRows.length > 1 && (allocation.delay_code || allocation.delay_minutes) ? (
              <button
                type="button"
                className="flight-delay-remove"
                onClick={() => removeRow(index)}
                title="Remove delay code"
                aria-label={`Remove delay code ${index + 1}`}
              >
                &times;
              </button>
            ) : null}

            <div className="fd-delay-grid">
              <div className="fd-delay-column">
                <div className="fd-delay-field">
                  <label className="fd-label fd-delay-label">Delay</label>
                  {readOnly ? (
                    <div className="fd-time-calc-box fd-time-calc-left flight-delay-reason-value">
                      {[allocation.delay_code, allocation.delay_description || selectedOption?.reason].filter(Boolean).join(' - ') || '--'}
                    </div>
                  ) : (
                    <div className="delay-dropdown fd-delay-dropdown">
                      <button
                        type="button"
                        className={`delay-dropdown-trigger ${allocation.delay_code ? 'has-value' : ''}`}
                        onClick={() => {
                          setOpenRow(current => current === index ? null : index);
                          setSearch('');
                        }}
                      >
                        <span className="delay-trigger-main">
                          {selectedOption ? `${selectedOption.code} - ${selectedOption.reason}` : 'Select delay code'}
                        </span>
                        <span className={`delay-trigger-arrow ${openRow === index ? 'open' : ''}`} aria-hidden="true">&#9662;</span>
                      </button>
                      <div className={`delay-dropdown-menu flight-delay-dropdown-menu ${openRow === index ? 'open' : 'closed'}`}>
                        <div className="flight-delay-search-wrap">
                          <input
                            type="search"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            placeholder="Search code or reason"
                            aria-label="Search delay codes"
                            autoFocus={openRow === index}
                          />
                        </div>
                        {filteredOptions.map(option => (
                          <button
                            key={option.code}
                            type="button"
                            className={`delay-dropdown-option ${allocation.delay_code === option.code ? 'active' : ''}`}
                            onClick={() => {
                              updateRow(index, {
                                delay_code: option.code,
                                delay_description: option.reason
                              });
                              setOpenRow(null);
                              setSearch('');
                            }}
                          >
                            <span className="delay-option-code">{option.code} - {option.reason}</span>
                            <span className="delay-option-desc">{option.description}</span>
                          </button>
                        ))}
                        {filteredOptions.length === 0 ? <div className="flight-delay-no-results">No delay codes found</div> : null}
                      </div>
                    </div>
                  )}
                </div>
                {isLastCard ? (
                  <div className="fd-delay-field">
                    <label className="fd-label fd-delay-label">Calculated Total Delay</label>
                    <div className="fd-time-calc-box fd-time-calc-left">{formatDelayMinutes(calculated)}</div>
                  </div>
                ) : null}
              </div>

              <div className="fd-delay-column">
                <div className="fd-delay-field">
                  <label className="fd-label fd-delay-label">Delay Time</label>
                  {readOnly ? (
                    <div className="fd-time-calc-box fd-time-calc-left">{formatDelayMinutes(allocation.delay_minutes)}</div>
                  ) : (
                    <div className="delay-minutes-wrap flight-delay-time-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-input fd fd-input"
                        placeholder="00:00"
                        value={allocation.time_input || ''}
                        onChange={event => commitTime(index, parseDelayClock(event.target.value))}
                        onBlur={event => commitTime(index, finalizeDelayClock(event.target.value))}
                      />
                    </div>
                  )}
                </div>
                {isLastCard ? (
                  <div className="fd-delay-field">
                    <label className="fd-label fd-delay-label">Entered Total Delay</label>
                    <div className="fd-time-calc-box fd-time-calc-left">{formatDelayMinutes(entered)}</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default FlightDelayAllocations;
